import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { channelLabel, type Channel } from '../../channels';
import { orgPath, workspacePath } from '../../nav';
import { useWorkspace } from '../Workspace';

interface WorkspaceGrant {
  id: string;
  identityId: string;
  username: string;
  userId: string;
  userEmail: string;
  userName: string;
  endsAt: string | null;
  channels: Channel[];
  grantedByEmail: string;
  createdAt: string;
  orgId: string;
  orgLabel: string;
  orgEnvironment: string;
}

interface Person {
  userId: string;
  name: string;
  email: string;
  grants: WorkspaceGrant[];
}

/**
 * Every live grant in the workspace, by person.
 *
 * Grants are made on an org, because that is where the Salesforce users they hand out live, and that is
 * the right place to make one. It is the wrong place to answer the question that comes up when someone
 * changes role or leaves: what can this person get into? With a dozen orgs that meant opening each one
 * and reading down its grant list, and the org you forgot to open was the one that mattered. This page
 * answers it in one place. It is read-only on purpose - a grant is still changed or revoked on its own
 * org, next to the Salesforce user and channels it names, so there is one way to do it rather than two
 * that can disagree.
 */
export function WorkspaceGrants() {
  const { tenantId, canManage } = useWorkspace();
  const [grants, setGrants] = useState<WorkspaceGrant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [org, setOrg] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!canManage) return;
    let live = true;
    setGrants(null);
    setError(null);
    api<{ grants: WorkspaceGrant[] }>(`/api/tenants/${tenantId}/grants`)
      .then((r) => live && setGrants(r.grants))
      .catch((err: unknown) => live && setError(err instanceof ApiError ? err.message : 'Could not load the grants here'));
    return () => {
      live = false;
    };
  }, [tenantId, canManage]);

  // The orgs to filter by are the ones that hold a grant: choosing one that holds none could only ever
  // show an empty page.
  const orgs = useMemo(() => {
    const seen = new Map<string, { id: string; label: string; environment: string }>();
    for (const g of grants ?? []) seen.set(g.orgId, { id: g.orgId, label: g.orgLabel, environment: g.orgEnvironment });
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [grants]);

  // The server already sorts by person, then org, so grouping keeps that order without sorting again.
  const people = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const byPerson = new Map<string, Person>();
    for (const g of grants ?? []) {
      if (org && g.orgId !== org) continue;
      if (needle && !`${g.userName} ${g.userEmail}`.toLowerCase().includes(needle)) continue;
      let p = byPerson.get(g.userId);
      if (!p) {
        p = { userId: g.userId, name: g.userName || g.userEmail, email: g.userEmail, grants: [] };
        byPerson.set(g.userId, p);
      }
      p.grants.push(g);
    }
    return [...byPerson.values()];
  }, [grants, org, search]);

  if (!canManage) {
    return <p className="muted">Only owners and admins can see the grants across this workspace.</p>;
  }
  if (!grants) return error ? <div className="alert">{error}</div> : <p className="muted">Loading…</p>;

  return (
    <>
      <p className="muted" style={{ marginTop: 0 }}>
        Grants are made and revoked on each org's Access grants page. Follow an org's name to change one.
      </p>

      {grants.length === 0 ? (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            Nobody holds access to any org here yet. Open an org from{' '}
            <Link to={workspacePath(tenantId)}>Salesforce orgs</Link> and grant it on its Access grants page.
          </p>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="row">
              <input
                type="search"
                aria-label="Search people"
                placeholder="Search by name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select aria-label="Org" value={org} onChange={(e) => setOrg(e.target.value)}>
                <option value="">Every org</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                    {o.environment === 'sandbox' ? ' (sandbox)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {people.length === 0 && <p className="muted">No grant matches that.</p>}

          {people.map((p) => (
            <section key={p.userId}>
              <h3>
                {p.name}
                {p.name !== p.email && <span className="small muted" style={{ fontWeight: 400 }}> · {p.email}</span>}
              </h3>
              <div className="card flush">
                <table>
                  <thead>
                    <tr>
                      <th>Org</th>
                      <th>Salesforce user</th>
                      <th>Channels</th>
                      <th>Ends</th>
                      <th>Granted by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.grants.map((g) => (
                      <tr key={g.id}>
                        <td>
                          <Link to={orgPath(tenantId, g.orgId, 'grants')}>{g.orgLabel}</Link>{' '}
                          <span className={`env-tag ${g.orgEnvironment}`}>
                            {g.orgEnvironment === 'sandbox' ? 'Sandbox' : 'Production'}
                          </span>
                        </td>
                        <td className="mono small">{g.username}</td>
                        <td className="small">{g.channels.map(channelLabel).join(', ')}</td>
                        <td className="small">{g.endsAt ? new Date(g.endsAt).toLocaleString() : 'No end date'}</td>
                        <td className="small">{g.grantedByEmail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </>
      )}
    </>
  );
}
